import React, { useMemo, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PageMeta from '../components/PageMeta';
import Reveal from '../components/Reveal';
import { CheckList, FinalCTA, PageHero, SectionHeading } from '../components/SharedSections';
import { providerMatrixTabs, providers } from '../data/siteData';

const { FiCheck, FiX, FiSearch, FiLayers } = FiIcons;

function MatrixTable({ rows }) {
  return (
    <div className="table-shell provider-matrix-shell" tabIndex="0" aria-label="Interactive provider and supplier matrix">
      <table className="comparison-table provider-matrix-table">
        <thead>
          <tr>
            <th>Feature</th>
            {providers.map((provider) => <th key={provider}>{provider}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.feature}>
              <th>{row.feature}</th>
              {row.values.map((value, index) => (
                <td key={`${row.feature}-${providers[index]}`}>
                  {typeof value === 'string' ? value : (
                    <>
                      <SafeIcon icon={value ? FiCheck : FiX} className={value ? 'matrix-check' : 'matrix-x'} aria-hidden="true" />
                      <span className="sr-only">{value ? 'Available' : 'Not shown as available'}</span>
                    </>
                  )}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProviderMatrix() {
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const activeRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return providerMatrixTabs[activeTab].rows.filter((row) => row.feature.toLowerCase().includes(query));
  }, [activeTab, search]);

  return (
    <>
      <PageMeta title="Provider & Supplier Matrix" description="Filter UCaaS provider features, AI capabilities, security, compliance, and CRM integrations with Cambia Digital." />
      <PageHero eyebrow="Interactive provider research" title="Go deeper than a standard provider comparison." text="Filter the details that matter to your business, then have Cambia verify current plan-level availability before you choose.">
        <div className="hero-check-card">
          <h2>Four decision lenses</h2>
          <CheckList items={['Core platform features', 'AI capabilities in practice', 'Compliance and security', 'CRM integrations']} />
        </div>
      </PageHero>
      <section className="section">
        <Reveal>
          <SectionHeading eyebrow="Provider & supplier matrix" title="Search the market by the requirements behind the sale." text="Use the tabs to focus your research. A checkmark is a starting point for a conversation, not a substitute for confirming the exact plan." />
        </Reveal>
        <Reveal className="matrix-controls">
          <div className="matrix-tabs" role="tablist" aria-label="Provider matrix categories">
            {providerMatrixTabs.map((tab, index) => (
              <button className={activeTab === index ? 'matrix-tab active' : 'matrix-tab'} key={tab.label} role="tab" aria-selected={activeTab === index} onClick={() => setActiveTab(index)}>
                <SafeIcon icon={index === 0 ? FiLayers : FiCheck} />
                {tab.label}
              </button>
            ))}
          </div>
          <label className="matrix-search">
            <span className="sr-only">Filter features</span>
            <SafeIcon icon={FiSearch} aria-hidden="true" />
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search features in this tab" />
          </label>
        </Reveal>
        <Reveal>
          <MatrixTable rows={activeRows} />
          {activeRows.length === 0 && <p className="matrix-empty">No features match that search in this tab.</p>}
          <p className="table-note">Feature, compliance, and integration availability can vary by plan, geography, and release — Cambia verifies current specifics during your free assessment.</p>
        </Reveal>
      </section>
      <section className="section tint">
        <Reveal><SectionHeading eyebrow="How to use this matrix" title="Research first. Validate the fit second." center /></Reveal>
        <div className="card-grid four">
          {['Needs assessment', 'Focused shortlist', 'Side-by-side comparison', 'Implementation support'].map((title, index) => (
            <Reveal className="process-card" key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{['Document workflows, users, integrations, and constraints.', 'Remove poor-fit platforms before demos take over your calendar.', 'Compare economics, administration, support, AI, and risk.', 'Coordinate porting, onboarding, training, and accountability.'][index]}</p></Reveal>
          ))}
        </div>
      </section>
      <FinalCTA title="Turn provider research into a confident decision." />
    </>
  );
}

export default ProviderMatrix;